import { DownOutlined } from '@ant-design/icons'
import { Dropdown, Menu, Button as ButtonAntd } from 'antd'
import { Button } from 'reactstrap'
import React, { useState, useRef, useEffect } from 'react'
import { compose } from 'redux'
import { useSelector, connect } from 'react-redux'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { connectResource } from '../../../common/utils/resource'
import { selectIsAmcLender, selectLenderId } from '../../../store/reducers/user/userSelectors'
import CommentTemplateModal from './CommentTemplateModal'
import CommentTemplateAccessModal from './CommentTemplateAccessModal'
import MessageTemplateSelectionModal from './MessageTemplateSelectionModal'
import MessageTemplateIcon from './MessageTemplateIcon'
import ModalTrigger from '../../../common/modals/ModalTrigger'
import { checkPermission } from '../../../common/utils/helpers'
import { selectAllOrderData } from '../../../store/reducers/order/orderSelectors'
import { getTemplatedFieldOptions } from './helpers'

const CommentTemplateDropdown = ({
  name,
  onHide,
  refreshOrder,
  setTextToInsert,
  templates,
  antdStyle,
  setSelectedTemplate,
  audienceFilter = null,
}) => {
  const [visible, setVisible] = useState(false)
  const [templateModalVisible, setTemplateModalVisible] = useState(false)
  const flags = useFlags()
  const isAmcLender = useSelector(selectIsAmcLender)
  const orderData = useSelector(selectAllOrderData)
  const viewMessageTemplates = checkPermission('view_message_templates')
  const templatesFetchedRef = useRef(false)

  const templatedFieldOptions = getTemplatedFieldOptions(isAmcLender)

  const setDropdownVisibility = (visible) => {
    setVisible(visible)
  }

  const setTemplatedTextToInsert = (text) => {
    let templatedText = text
    templatedFieldOptions.forEach((option) => {
      let value = option.getValue ? option.getValue(orderData) : orderData[option.value]
      if (option.formatter) {
        value = option.formatter(value)
      }
      templatedText = templatedText.replace(`{{${option.id}}}`, value)
    })
    setTextToInsert(templatedText, 'template')
  }


  const menu = [
    checkPermission('edit_message_templates') && (
      <Menu.Item key="0" className="order-action-dropdown-div">
        <ModalTrigger
          component={CommentTemplateModal}
          onHide={onHide}
          templates={templates}
          refreshOrder={refreshOrder}
          templatedFieldOptions={templatedFieldOptions}
        >
          <div className="border border-light" color="light">
            <Button className="text-left order-dropdown-button" block>
              Create New Message Template
            </Button>
          </div>
        </ModalTrigger>
      </Menu.Item>
    ),
    checkPermission('edit_message_templates') && (
      <Menu.Item key="1" className="order-action-dropdown-div">
        <ModalTrigger
          component={CommentTemplateAccessModal}
          onHide={onHide}
          templates={templates}
          refreshOrder={refreshOrder}
          editing={true}
          templatedFieldOptions={templatedFieldOptions}
        >
          <div className="border border-light" color="light">
            <Button className="text-left order-dropdown-button" block>
              Edit Message Templates
            </Button>
          </div>
        </ModalTrigger>
      </Menu.Item>
    ),
    <Menu.Item key="2" className="order-action-dropdown-div">
      <ModalTrigger
        component={CommentTemplateAccessModal}
        onHide={onHide}
        templates={templates}
        refreshOrder={refreshOrder}
        setDropdownVisibility={setDropdownVisibility}
        setTextToInsert={setTemplatedTextToInsert}
        templatedFieldOptions={templatedFieldOptions}
      >
        <div className="border border-light" color="light">
          <Button className="text-left order-dropdown-button" block>
            Insert Message Template
          </Button>
        </div>
      </ModalTrigger>
    </Menu.Item>,
  ]

  useEffect(() => {
    if (viewMessageTemplates && !templatesFetchedRef.current) {
      templates.fetch()
      templatesFetchedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMessageTemplates])

  const handleTemplateIconClick = () => {
    // Fetch templates if not loaded
    if (templates && !templates.data && !templates.isLoading) {
      templates.fetch()
    }
    setTemplateModalVisible(true)
  }

  const handleTemplateModalCancel = () => {
    setTemplateModalVisible(false)
  }

  const handleTemplateSelect = (templateText, template) => {
    setSelectedTemplate?.(template)
    setTemplatedTextToInsert(templateText)
    setTemplateModalVisible(false)
  }

  // Render new UX with MessageTemplateIcon and Modal
  if (viewMessageTemplates && flags?.useUpdatedMessageTemplatesUx) {
    return (
      <>
        <MessageTemplateIcon onClick={handleTemplateIconClick} />
        <MessageTemplateSelectionModal
          onCancel={handleTemplateModalCancel}
          onTemplateSelect={handleTemplateSelect}
          orderData={orderData}
          audienceFilter={audienceFilter}
          templates={templates?.data?.templates || []}
          visible={templateModalVisible}
        />
      </>
    )
  }

  // Render legacy dropdown UX
  return (
    viewMessageTemplates && !flags?.useMessageTemplateManagementSettings && !flags?.useUpdatedMessageTemplatesUx && (
      <Dropdown
        overlay={<Menu name={name}>{menu}</Menu>}
        trigger={['click']}
        onVisibleChange={setDropdownVisibility}
        visible={visible}
      >
        {antdStyle ? (
          <ButtonAntd>
            {' '}
              Manage Message Templates <DownOutlined />
          </ButtonAntd>
        ) : (
          <Button className="mt-1 mr-auto">
            {' '}
              Manage Message Templates <DownOutlined />
          </Button>
        )}
      </Dropdown>
    )
  )
}

const mapStateToProps = state => ({
  lenderId: selectLenderId(state),
})

// Wrapper component to handle feature flag
const CommentTemplateDropdownWrapper = (props) => {
  const flags = useFlags() || {}
  const useSecureEndpoint = flags?.plat2773UseSecureMessageTemplateEndpoint || false

  // Dynamically create the connected component based on the flag
  const ConnectedComponent = React.useMemo(() => {
    return compose(
      connect(mapStateToProps),
      connectResource({
        namespace: 'templates',
        endpoint: useSecureEndpoint
          ? 'lender/messageTemplates'
          : 'lender/messageTemplate/:lenderId',
        prefetch: false,
        apiVersion: 2,
      })
    )(CommentTemplateDropdown)
  }, [useSecureEndpoint])

  return <ConnectedComponent {...props} />
}

export default CommentTemplateDropdownWrapper
